/** @jsxImportSource theme-ui */
import { Box, Text, Heading, Flex } from 'theme-ui';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';
import { JSONTree } from 'react-json-tree';
import { useDialogState, Dialog, DialogBackdrop } from 'reakit/Dialog';
import jsonTreeTheme from '@/theme/jsonTreeTheme';
import {
    useGetVossSubscriptionsQuery,
    useAddDiscountAgreementsToSubscriptionMutation,
    useGetDiscountAgreementsQuery,
} from '@/store/apiService';
import Button from '@/components/Button';
import { tableColumns as discountAgreementTableColumns } from '../dicountAgreements/DiscountAgreements';
import { useEffect } from 'react';
import { toast } from 'react-toastify';
import DataTable from '@/components/DataTable';
import SubscriptionDiscounts from './SubscriptionDiscounts';
import SubscriptionLicenses from './SubscriptionLicenses';

const columns = [
    {
        id: 'actions',
        columnKey: 'Actions',
        accessor: 'Actions',
        Cell: ({ cell, row }) => {
            const dialog = useDialogState();
            const { id } = useParams();
            const discountAgreementsState = useGetDiscountAgreementsQuery({});
            const discountAgreements = discountAgreementsState.data
                ? discountAgreementsState.data.filter((d) => d.Status === 'Published')
                : [];

            if (row.original.status === 'Terminated') return null;

            return (
                <>
                    <DialogBackdrop {...dialog}>
                        <Dialog
                            sx={{
                                width: '85%',
                                maxWidth: 'max-content',
                                '&&': {
                                    padding: 4,
                                    minHeight: '480px',
                                    overflow: 'scroll',
                                },
                            }}
                            {...dialog}
                        >
                            <Box
                                sx={{
                                    '& > ul': {
                                        padding: (t: any) => `${t.space[2]} !important`,
                                        borderRadius: (t: any) => `${t.radii['sm']} !important`,
                                    },
                                }}
                            >
                                <Heading as="h2">Add discount agreement</Heading>
                                <DataTable
                                    columns={[
                                        ...discountAgreementTableColumns,
                                        {
                                            accessor: 'Actions',
                                            Cell: ({ cell, row }) => {
                                                const [
                                                    connectDiscountAgreement,
                                                    connectDiscountAgreementState,
                                                ] =
                                                    useAddDiscountAgreementsToSubscriptionMutation();

                                                useEffect(() => {
                                                    if (connectDiscountAgreementState.isSuccess) {
                                                        dialog.hide();
                                                        toast.success('Success');
                                                    }
                                                }, [connectDiscountAgreementState.isSuccess]);

                                                return (
                                                    <Button
                                                        variant="secondary"
                                                        onClick={() => {
                                                            connectDiscountAgreement({
                                                                // CompanyOwnerId: values.companyId,
                                                                CompanyId: id,
                                                                DiscountAgreements: {
                                                                    DiscountAgreementId:
                                                                        row.original.Id,
                                                                },
                                                            });
                                                        }}
                                                        isLoading={
                                                            connectDiscountAgreementState.isLoading
                                                        }
                                                    >
                                                        Select
                                                    </Button>
                                                );
                                            },
                                        },
                                    ]}
                                    data={discountAgreements}
                                />
                            </Box>
                        </Dialog>
                    </DialogBackdrop>
                    <Flex
                        sx={{
                            justifyContent: 'flex-end',
                            flexDirection: 'column',
                            gap: 2,
                            flexGrow: 1,
                            alignItems: 'center',
                            a: { display: 'flex' },
                        }}
                    >
                        <div></div>
                        <Button
                            sx={{ width: '100%' }}
                            variant="secondary"
                            onClick={() => {
                                dialog.show();
                            }}
                        >
                            <Text>Add discount agreement</Text>
                        </Button>
                    </Flex>
                </>
            );
        },
    },
    {
        columnKey: 'id',
        accessor: 'id',
        Header: 'Id',
        width: '100px',
        Cell: ({ row, cell }) => (
            <Box sx={{ width: '100px', whiteSpace: 'break-spaces' }}>{cell.value}</Box>
        ),
    },
    {
        columnKey: 'customer',
        accessor: 'customer',
        Header: 'Customer',
        Cell: ({ row, cell }) => {
            return (
                <Box
                    sx={{
                        '& > ul': {
                            padding: (t: any) => `${t.space[2]} !important`,
                            borderRadius: (t: any) => `${t.radii['sm']} !important`,
                        },
                    }}
                >
                    <JSONTree
                        data={cell.value}
                        hideRoot={true}
                        theme={jsonTreeTheme}
                        invertTheme={true}
                        shouldExpandNodeInitially={(keyPath, data, level) => {
                            return true;
                        }}
                    />
                </Box>
            );
        },
    },
    {
        columnKey: 'package',
        accessor: 'package',
        Header: 'Package',
        Cell: ({ row, cell }) => {
            return (
                <Box
                    sx={{
                        '& > ul': {
                            padding: (t: any) => `${t.space[2]} !important`,
                            borderRadius: (t: any) => `${t.radii['sm']} !important`,
                        },
                    }}
                >
                    <JSONTree
                        data={cell.value}
                        hideRoot={true}
                        theme={jsonTreeTheme}
                        invertTheme={true}
                        shouldExpandNodeInitially={(keyPath, data, level) => {
                            return true;
                        }}
                    />
                </Box>
            );
        },
    },
    {
        columnKey: 'tier',
        accessor: 'tier',
        Header: 'Tier',
        Cell: ({ row, cell }) => {
            return (
                <Box
                    sx={{
                        '& > ul': {
                            padding: (t: any) => `${t.space[2]} !important`,
                            borderRadius: (t: any) => `${t.radii['sm']} !important`,
                        },
                    }}
                >
                    <JSONTree
                        data={cell.value}
                        hideRoot={true}
                        theme={jsonTreeTheme}
                        invertTheme={true}
                        shouldExpandNodeInitially={(keyPath, data, level) => {
                            if (level < 3) {
                                return true;
                            }
                        }}
                    />
                </Box>
            );
        },
    },
    {
        columnKey: 'plan',
        accessor: 'plan',
        Header: 'Plan',
        Cell: ({ row, cell }) => {
            return (
                <Box
                    sx={{
                        '& > ul': {
                            padding: (t: any) => `${t.space[2]} !important`,
                            borderRadius: (t: any) => `${t.radii['sm']} !important`,
                        },
                    }}
                >
                    <JSONTree
                        data={cell.value}
                        hideRoot={true}
                        theme={jsonTreeTheme}
                        invertTheme={true}
                        shouldExpandNodeInitially={(keyPath, data, level) => {
                            return true;
                        }}
                    />
                </Box>
            );
        },
    },
    {
        columnKey: 'productGroups',
        accessor: 'productGroups',
        Header: 'Product groups',
        Cell: ({ row, cell }) => {
            return (
                <Box
                    sx={{
                        '& > ul': {
                            padding: (t: any) => `${t.space[2]} !important`,
                            borderRadius: (t: any) => `${t.radii['sm']} !important`,
                        },
                    }}
                >
                    <JSONTree
                        data={cell.value}
                        hideRoot={true}
                        theme={jsonTreeTheme}
                        invertTheme={true}
                        shouldExpandNodeInitially={(keyPath, data, level) => {
                            if (level < 2) {
                                return true;
                            }
                        }}
                    />
                </Box>
            );
        },
    },
    {
        columnKey: 'bindingPeriod',
        accessor: 'bindingPeriod',
        Header: 'Binding period',
        Cell: ({ row, cell }) => {
            return (
                <Box
                    sx={{
                        '& > ul': {
                            padding: (t: any) => `${t.space[2]} !important`,
                            borderRadius: (t: any) => `${t.radii['sm']} !important`,
                        },
                    }}
                >
                    <JSONTree
                        data={cell.value}
                        hideRoot={true}
                        theme={jsonTreeTheme}
                        invertTheme={true}
                        shouldExpandNodeInitially={(keyPath, data, level) => {
                            return true;
                        }}
                    />
                </Box>
            );
        },
    },
    {
        columnKey: 'subscriptionBillingPeriod',
        accessor: 'subscriptionBillingPeriod',
        Header: 'Subscription billing period',
        Cell: ({ row, cell }) => {
            return (
                <Box
                    sx={{
                        '& > ul': {
                            padding: (t: any) => `${t.space[2]} !important`,
                            borderRadius: (t: any) => `${t.radii['sm']} !important`,
                        },
                    }}
                >
                    <JSONTree
                        data={cell.value}
                        hideRoot={true}
                        theme={jsonTreeTheme}
                        invertTheme={true}
                        shouldExpandNodeInitially={(keyPath, data, level) => {
                            return true;
                        }}
                    />
                </Box>
            );
        },
    },
    {
        columnKey: 'usageBillingPeriod',
        accessor: 'usageBillingPeriod',
        Header: 'Usage billing period',
        Cell: ({ row, cell }) => {
            return (
                <Box
                    sx={{
                        '& > ul': {
                            padding: (t: any) => `${t.space[2]} !important`,
                            borderRadius: (t: any) => `${t.radii['sm']} !important`,
                        },
                    }}
                >
                    <JSONTree
                        data={cell.value}
                        hideRoot={true}
                        theme={jsonTreeTheme}
                        invertTheme={true}
                        shouldExpandNodeInitially={(keyPath, data, level) => {
                            return true;
                        }}
                    />
                </Box>
            );
        },
    },
    {
        columnKey: 'created',
        accessor: 'created',
        Header: 'Created',
        Cell: ({ cell }) => {
            if (!cell.value) return '';
            return format(new Date(cell.value), 'd MMM yyyy, p');
        },
    },
    { columnKey: 'externalId', accessor: 'externalId', Header: 'External Id' },
];

const CompanySubscriptions = () => {
    const { id } = useParams();
    const subscriptions = useGetVossSubscriptionsQuery({ companyId: id });

    return (
        <Box>
            <Heading>Company subscriptions</Heading>
            <Box
                variant="segment"
                sx={{
                    borderRadius: 'sm',
                    overflowY: 'scroll',
                    zIndex: 2,
                    justifySelf: 'left',
                    width: '100%',
                    backgroundColor: 'background',
                    padding: 2,
                    position: 'relative',
                    table: {
                        display: 'block',
                        overflowX: 'auto',
                        whiteSpace: 'nowrap',
                    },
                    thead: {
                        textAlign: 'center',
                    },
                }}
            >
                <DataTable
                    data={subscriptions.data}
                    columns={columns}
                    isLoading={subscriptions.isLoading}
                    isFetching={subscriptions.isFetching}
                />
            </Box>
            {subscriptions.data?.map((subscription) => (
                <Box key={subscription.id} sx={{ marginTop: 3 }}>
                    <Heading as="h2">
                        Subscription {subscription.tier?.name} ({subscription.id})
                        {subscription.status ? ` - ${subscription.status}` : ''}
                    </Heading>
                    <Heading as="h3">Discount agreements</Heading>
                    <SubscriptionDiscounts id={subscription.id} />
                    <Heading as="h3">Licenses</Heading>
                    <SubscriptionLicenses id={subscription.id} />
                </Box>
            ))}
        </Box>
    );
};

export default CompanySubscriptions;
